import React, { useRef, useCallback } from 'react';
import { ReactEditor } from 'slate-react';
import styles from '../../Toolbar/Toolbar.module.css';
import ColorPicker from '../../ColorPicker/ColorPicker';
import classnames from 'classnames';
import { type ToolbarSubmenuProps } from '../../../types/aha-editor';
import type ToolMark from '../../../plugins/Formatting/tools/ToolMark';
import MarkUtils from '../../../AhaEditorUtils/MarkUtils';
import ColorUtils from '../../../util/color';

const DEFAULT_COLOR = '#000000';

interface ColorPickerToolButtonProps
  extends ToolbarSubmenuProps<typeof ToolMark> {
  showCustomColorInput?: boolean;
  showClearButton?: boolean;
}

const ColorPickerToolButton: React.FC<ColorPickerToolButtonProps> = ({
  editor,
  controller,
  tool,
  isSubmenu,
  showCustomColorInput = true,
  showClearButton = true,
}) => {
  const menuRef = useRef<HTMLElement>();
  const color = MarkUtils.getMarkAtSelection(editor, tool.mark);
  const mappedColor = color
    ? ColorUtils.mapColorFromColorPickerToDarkMode(color)
    : undefined;
  const defaultColor = ColorUtils.darkMode()
    ? ColorUtils.mapColorFromColorPickerToDarkMode(DEFAULT_COLOR)
    : DEFAULT_COLOR;

  const handleColorChange = useCallback(
    (color: string, focusEditor?: boolean) => {
      const mappedColor = ColorUtils.mapColorFromColorPickerFromDarkMode(color);

      tool.addMark(editor, controller, mappedColor);
      if (focusEditor) {
        ReactEditor.focus(editor);
      }
    },
    [controller, editor, tool]
  );
  const handleClearColor = useCallback(() => {
    tool.removeMark(editor, controller);
  }, [controller, editor, tool]);

  const handleCloseMenu = useCallback(() => {
    menuRef.current?.removeAttribute('open');
    menuRef.current?.closest('aha-menu-content[open]')?.removeAttribute('open');
  }, []);

  const style = mappedColor ? { color: mappedColor } : {};

  return (
    <aha-menu submenu={isSubmenu} ref={menuRef}>
      <div
        slot="control"
        className={classnames({
          [styles.toolbarButtonDisabled]: !tool.isEnabled({
            editor,
            controller,
          }),
        })}
      >
        <i className={classnames('fa-fw', tool.iconClass)} style={style} />
        {isSubmenu && tool.label}
      </div>
      <aha-menu-content>
        <ColorPicker
          currentColor={mappedColor ?? defaultColor}
          handleColorChange={handleColorChange}
          handleClearColor={handleClearColor}
          handleCloseMenu={handleCloseMenu}
          showCustomColorInput={showCustomColorInput}
          showClearButton={showClearButton}
        />
      </aha-menu-content>
    </aha-menu>
  );
};

export default ColorPickerToolButton;
